﻿import { FormValidator, PhoneValidator } from './FormValidator.js';

/** A form viewmodel which intercepts the submit event, performs validation, and cancels the submit
 * if validation fails. If it fails, a Bootstrap compatible class name ("was-validated") is added to
 * the form element. Uses a FormValidator object for the validation. */
export class FormViewModel {
  constructor(config) {
    this.config = config;
    this.form = document.getElementById(config.UI.form);
    this.form.addEventListener('submit', (e) => this.validateForm(e));
    this.formValidator = new FormValidator();
  }

  validateForm(e) {
    /* Call our own validation method first to ensure state and messages are up to date. If any of
     * our validation checks fail, then we call input.setCustomValidity(msg) which will cause the
     * paymentForm.checkValidity() call to return false as well. */
    this.formValidator.checkCustomValidity();
    // Call the built in validation:
    if (!this.form.checkValidity()) {
      e.preventDefault();
      e.stopPropagation();
      // Ensure the visible feedback elements have the latest validation messages:
      this.formValidator.updateFeedbackElements();
    }
    this.form.classList.add('was-validated');
  }
}

/** ViewModel for the RegisterUser form. */
export class RegisterUser extends FormViewModel {
  constructor(config) {
    super(config);
    const byId = id => document.getElementById(id);
    this.formValidator.add(byId(config.UI.firstName));
    this.formValidator.add(byId(config.UI.lastName));
    this.formValidator.add(byId(config.UI.email));
    this.phoneValidator = PhoneValidator.createValidator(config.countryCode);

    const invalidPhoneMsg = 'Invalid phone number';
    this.formValidator.add(byId(config.UI.phone),
      () => invalidPhoneMsg, input => this.phoneValidator.validate(input.value));

    let validator = this.formValidator.add(byId(config.UI.password));
    // Hide the server generated error message as soon as we start typing:
    validator.onCustomValidate = input => !input.classList.contains('is-invalid');
    validator.onGetMessage = () => null;

    validator = this.formValidator.add(byId(config.UI.confirmPassword));
    // Hide the server generated error message as soon as we start typing:
    validator.onCustomValidate = input => !input.classList.contains('is-invalid');
    validator.onGetMessage = () => null;

    /* The createProfileBtn and acceptPolicyCheckBox only exists when the form is used for creating
     * new users. When showing the profile for an existing user, e.g. after login, these two
     * elements are not rendered. */
    this.createProfileBtn = byId(config.UI.createProfileBtn);
    if (this.createProfileBtn) {
      this.acceptPolicyCheckBox = byId(config.UI.acceptPolicyCheckBox);
      if (this.acceptPolicyCheckBox.checked) {
        this.createProfileBtn.removeAttribute('disabled');
      } else {
        this.createProfileBtn.setAttribute('disabled', '');
      }
      this.acceptPolicyCheckBox.addEventListener('change', (event) => {
        this.createProfileBtn.toggleAttribute('disabled');
      });
    }
  }
}

/** ViewModel for the Login form. */
export class Login extends FormViewModel {
  constructor(config) {
    super(config);
    const byId = id => document.getElementById(id);
    this.formValidator.add(byId(config.UI.email));
    const passwordInput = byId(config.UI.password);
    const validator = this.formValidator.add(passwordInput);
    // Hide the server generated error message as soon as we start typing:
    validator.onCustomValidate = input => !input.classList.contains('is-invalid');
    validator.onGetMessage = () => null;

    const popup = jQuery('#resetPwdPopup'); // Popup showing Reset Password form

    // Add event handler for when the popup is closed -> if "Send email now", then block submission if the form is invalid
    popup.on('hide.bs.modal', function (e) {
      if (document.activeElement && document.activeElement.name === 'okBtn') {
        var form = document.getElementById('resetPwd-form');
        var target = document.getElementById('resetPwd-email');
        if (target !== null) {
          if (!form.reportValidity() || target.value === null || target.value === '') {
            e.preventDefault();
          } else {
            form.submit();
          }
        }
      }
    });

    // Add event handler for when the popup is shown -> copy email address from login form to popup
    popup.on('shown.bs.modal', function (e) {
      var source = document.getElementById('LoginEmail');
      var target = document.getElementById('resetPwd-email');
      if (source !== null && target !== null) {
        target.value = source.value;
      };
    });

    /* Try to find the password clear text toggler icon, and if found add an event handler for
     * toggling it: */
    const eyeIcon = document.querySelector(config.UI.eyeIconSelector);
    if (eyeIcon) {
      eyeIcon.addEventListener('click', (e) => {
        // Toggles the type of the password input element:
        const type = passwordInput.getAttribute('type') === 'password' ? 'text' : 'password';
        passwordInput.setAttribute('type', type);
        /* If we had both eye and eye-slash icons (our feather icons only have one eye icon), we
         * could've done something like this (example uses Bootstrap icons): */
        //eyeIcon.classList.toggle('bi-eye');
        //eyeIcon.classList.toggle('bi-eye-slash');
      });
    }
  }
}

/** ViewModel for the LookupOrder form. */
export class LookupOrder extends FormViewModel {
  constructor(config) {
    super(config);
    const byId = id => document.getElementById(id);
    this.formValidator.add(byId(config.UI.email));
    const validator = this.formValidator.add(byId(config.UI.orderNo));
    // Hide the server generated error message as soon as we start typing:
    validator.onCustomValidate = input => !input.classList.contains('is-invalid');
    validator.onGetMessage = () => null;
  }
}
